import { get, postApi, post, put, del } from "@/shared/utils/fetchApi";
import {
  Subscription,
  CreateSubscriptionRequest,
  UpdateSubscriptionRequest,
  IncrementConsumptionRequest,
  PaymentCheckResponse,
  PaymentCheckData,
  GetMySubscriptionResponse,
} from "../types/subscription-types";

interface SubscriptionsResponse {
  success: boolean;
  data: Subscription[];
}

interface SubscriptionResponse {
  success: boolean;
  data: Subscription;
  message?: string;
}

interface DeleteResponse {
  success: boolean;
  message: string;
}

export const SubscriptionService = {
  /**
   * Lista todas as assinaturas (com filtros opcionais)
   */
  async getSubscriptions(params?: {
    organization_id?: string;
    plan_id?: string;
    status?: string;
  }): Promise<SubscriptionsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) {
      queryParams.append("organization_id", params.organization_id);
    }
    if (params?.plan_id) {
      queryParams.append("plan_id", params.plan_id);
    }
    if (params?.status) {
      queryParams.append("status", params.status);
    }

    const query = queryParams.toString();
    const endpoint = query ? `/subscriptions?${query}` : "/subscriptions";

    const data = await get<Subscription[]>(endpoint);
    return { success: true, data };
  },

  /**
   * Busca a assinatura ativa da organização do usuário logado
   */
  async getMySubscription(): Promise<GetMySubscriptionResponse> {
    // GET /subscriptions/my retorna Subscription raw (snake_case + JSONB
    // subscribed_plan/consumption); legacy: { success, data }.

    const data = await get<Subscription>("/subscriptions/my");
    return { success: true, data };
  },

  /**
   * Busca uma assinatura por ID
   */
  async getSubscriptionById(id: string): Promise<SubscriptionResponse> {
    const data = await get<Subscription>(`/subscriptions/${id}`);
    return { success: true, data };
  },

  /**
   * Cria uma nova assinatura.
   *
   * `postApi` de propósito: a raiz expõe `GET /subscriptions`, `PUT /:id`,
   * `POST /:id/cancel` e `DELETE /:id` (`apps/api/start/routes/subscriptions.ts`),
   * mas não a criação — ela segue só sob `/api`, com o envelope
   * `{success, data}` que o tipo de retorno já declara.
   */
  async createSubscription(
    data: CreateSubscriptionRequest,
  ): Promise<SubscriptionResponse> {
    return await postApi<SubscriptionResponse>("/subscriptions", data);
  },

  /**
   * Atualiza uma assinatura existente
   */
  async updateSubscription(
    id: string,
    data: UpdateSubscriptionRequest,
  ): Promise<SubscriptionResponse> {
    const updated = await put<Subscription>(`/subscriptions/${id}`, data);
    return { success: true, data: updated };
  },

  /**
   * Incrementa o consumo de uma métrica específica.
   *
   * Também sob `/api`: não há `POST /subscriptions/:id/increment` na raiz.
   */
  async incrementConsumption(
    id: string,
    data: IncrementConsumptionRequest,
  ): Promise<SubscriptionResponse> {
    return await postApi<SubscriptionResponse>(
      `/subscriptions/${id}/increment`,
      data,
    );
  },

  /**
   * Cancela uma assinatura
   */
  async cancelSubscription(id: string): Promise<SubscriptionResponse> {
    const data = await post<Subscription>(`/subscriptions/${id}/cancel`, {});
    return { success: true, data };
  },

  /**
   * Verifica o status de pagamento e acesso da subscription
   */
  async paymentCheck(): Promise<PaymentCheckResponse> {
    // v2 retorna DTO flat com `message` no topo + campos planos;
    // legacy retorna `{ success, message, data: { ... } }`.
    const flat = await get<PaymentCheckData & { message?: string }>(
      "/subscriptions/payment-check",
    );
    const { message, ...data } = flat;
    return { success: true, message, data: data as PaymentCheckData };
  },

  /**
   * Deleta uma assinatura
   */
  async deleteSubscription(id: string): Promise<DeleteResponse> {
    // v2 retorna 204 No Content; legacy retorna `{ success, message }`.
    await del<void>(`/subscriptions/${id}`);
    return { success: true, message: "Subscription deleted" };
  },
};
